<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import CountryFlag from '@/shared/components/CountryFlag/CountryFlag.vue'
  import BaseCard from '@/shared/components/BaseCard/BaseCard.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import type { Country } from '@/shared/types/api.types'
  import { usePerformanceTiming, useValidation, useValidators } from '@/shared/composables'
  import {
    DEFAULT_TITLE_KEY,
    DEFAULT_VALUE_KEY,
    PROCEED_EVENT_NAME,
    DEFAULT_SEARCH_KEY,
  } from './FlowResidenceWidget.constants'
  import { CountryBoundType } from '@/shared/types/api.types'
  import NewSelect from '@/widgets/Flow/NewBaseSelect/NewBaseSelect.vue'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import type { Locale } from '@/shared/types/common.types'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: 'load', request: () => Promise<any>): void
  }

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { locale } = useI18n()
  const { residenceCountryValidator } = useValidators()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const selectedOption = ref(userStore.residenceCountry?.id || '')
  const listOfDestionations = ref<Country[]>([])
  const options = ref<Country[]>([])
  const isLoading = ref(true)

  // TODO: Add requests cancelation on component unmount

  const validationRules = {
    selectedOption: {
      residenceCountryValidator,
    },
  }

  const { v$, errors } = useValidation(validationRules, { selectedOption })

  const onProceed = async () => {
    if (options.value.length === 0) return

    v$.value.$touch()

    if (!v$.value.$error) {
      const country = options.value.find((option) => option.id === selectedOption.value)

      if (!country) return

      userStore.setResidenceCountry(
        {
          id: country.id,
          name: country.name,
        },
        true
      )

      emit('load', async () => {
        await commonStore.sendFormData({
          originCode: country.id,
          originName: country.name,
        })

        gaService.sendSelectCountryProceed(country.name)

        emit(PROCEED_EVENT_NAME)
      })
    }
  }

  onMounted(async () => {
    emit('load', async () => {
      listOfDestionations.value = await userStore.fetchCountries(
        CountryBoundType.INBUND,
        locale.value as Locale
      )
      options.value = listOfDestionations.value
      isLoading.value = false

      onPerformanceCheckEnd()
    })
  })

  const visitorsHref = `${window.location.origin}/${locale.value}/visitors`
</script>

<template>
  <FlowCard :title="$t('inbound_from_header')" with-button @button-click="onProceed">
    <template #header>
      <BaseCard class="residence-card-warning">
        <p>
          {{ $t('inbound.country_of_residence.mandatory:insurance_wordings.start') }}
          <a :href="visitorsHref" @click="gaService.sendToVisitors()" target="_blank">
            {{ $t('inbound.country_of_residence.mandatory:insurance_wordings.link') }}
          </a>
          {{ $t('inbound.country_of_residence.mandatory:insurance_wordings.end') }}
        </p>
      </BaseCard>
    </template>

    <div class="flow-residence-content">
      <NewSelect
        :label="$t('home_form_inbound_destination_label')"
        v-model="selectedOption"
        :errors="errors.selectedOption"
        :disabled="isLoading"
        :options="options"
        :option-title="DEFAULT_TITLE_KEY"
        :option-value="DEFAULT_VALUE_KEY"
        :option-search="DEFAULT_SEARCH_KEY"
        filterable
        big
      >
        <template #option="option">
          <div class="flow-residence-option">
            <CountryFlag :iso="option.isocode" class="option-flag" />
            <span class="option-title">{{ option.name }}</span>
          </div>
        </template>
      </NewSelect>
    </div>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowResidenceWidget';
</style>
